import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Button, Spinner, Modal, ListGroup, Row, Col } from 'react-bootstrap';
import { Trash2, Eye, ShieldCheck, ArrowLeft, MessageSquare, History, Code } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHistory, setSelectedHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL;
    // Theme state
    const [theme] = useState(localStorage.getItem('appTheme') || 'dark');
    const navigate = useNavigate();

    // Aapka Fix Admin Email
    const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "";

    // fetchUsers ko useCallback mein wrap kiya warnings hatane ke liye
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (err) { 
            console.error("Fetch error:", err);
            navigate('/login');
        }
        setLoading(false);
    }, [navigate, API_URL]);

    useEffect(() => {
        const userEmail = localStorage.getItem('userEmail');
        // Security check
        if (!userEmail || userEmail !== ADMIN_EMAIL) {
            alert("Unauthorized Access!");
            navigate('/dashboard');
        } else {
            fetchUsers();
        }
    }, [navigate, fetchUsers, ADMIN_EMAIL]);

    const fetchUserHistory = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/admin/user/${userId}/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedHistory(res.data);
            setShowHistory(true);
        } catch (err) { alert("Error fetching history"); }
    };

    const deleteUser = async (id) => {
        if (!window.confirm("Delete this user permanently?")) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/admin/user/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchUsers();
        } catch (err) { alert("Delete failed"); }
    };

    return (
        <Container fluid className={`min-vh-100 py-4 ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}`}>
            <Container>
                {/* Header Section */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3 text-center text-md-start">
                    <h2 className={`fw-bold d-flex align-items-center ${theme === 'dark' ? 'text-info' : 'text-primary'}`}>
                        <ShieldCheck className="me-2" size={32}/> Admin Dashboard
                    </h2>
                    <Button 
                        variant={theme === 'dark' ? "outline-light" : "outline-dark"} 
                        onClick={() => navigate('/dashboard')}
                        className="rounded-pill px-4 shadow-sm"
                    >
                        <ArrowLeft size={18} className="me-1"/> Back to Dashboard
                    </Button>
                </div>

                {/* Main Table Section */}
                {loading ? (
                    <div className="text-center mt-5 pt-5">
                        <Spinner animation="border" variant="info" />
                        <p className="mt-2">Loading users...</p>
                    </div>
                ) : (
                    <div className="card shadow-lg overflow-hidden">
                        <Table 
                            striped 
                            bordered={theme === 'light'} 
                            hover 
                            responsive 
                            variant={theme === 'dark' ? 'dark' : 'light'} 
                            className="mb-0 align-middle"
                        >
                            <thead className={theme === 'dark' ? 'bg-black text-info border-info' : 'bg-primary text-white border-primary'}>
                                <tr>
                                    <th className="py-3">ID</th>
                                    <th className="py-3">User Name</th>
                                    <th className="py-3">Email Address</th>
                                    <th className="py-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length > 0 ? users.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.id}</td>
                                        <td className="fw-bold">{user.name}</td>
                                        <td>{user.email}</td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Button variant="info" size="sm" className="text-white shadow-sm" onClick={() => fetchUserHistory(user.id)}>
                                                    <Eye size={14} className="me-1"/> <span className="d-none d-lg-inline">View Chats</span>
                                                </Button>
                                                <Button variant="danger" size="sm" className="shadow-sm" onClick={() => deleteUser(user.id)}>
                                                    <Trash2 size={14}/>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="4" className="text-center py-4 text-muted">No users registered yet.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* History Modal */}
                <Modal 
                    show={showHistory} 
                    onHide={() => setShowHistory(false)} 
                    centered 
                    size="lg"
                    contentClassName={theme === 'dark' ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                >
                    <Modal.Header closeButton closeVariant={theme === 'dark' ? 'white' : 'dark'} className="border-secondary px-4">
                        <Modal.Title className="fw-bold d-flex align-items-center text-warning">
                            <History size={24} className="me-2"/> User Chat History
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-0">
                        {selectedHistory.length > 0 ? (
                            <ListGroup variant="flush">
                                {selectedHistory.map(chat => (
                                    <ListGroup.Item 
                                        key={chat.id} 
                                        className={`${theme === 'dark' ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'} px-4 py-3`}
                                    >
                                        <Row className="align-items-center">
                                            <Col xs={1} className="text-center p-0">
                                                <Code size={20} className="text-primary opacity-75" />
                                            </Col>
                                            <Col xs={8} md={9}>
                                                <div className="text-truncate fw-bold">{chat.title}</div>
                                                <small className="text-muted d-block small">Created: {new Date(chat.created_at).toLocaleString()}</small>
                                            </Col>
                                            <Col xs={3} md={2} className="text-end">
                                                <MessageSquare size={16} className="text-info opacity-50" />
                                            </Col>
                                        </Row>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <div className="text-center py-5 opacity-50">
                                <MessageSquare size={48} className="mb-2"/>
                                <p>No chats found for this user.</p>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-secondary px-4">
                        <Button variant={theme === 'dark' ? "outline-light" : "secondary"} onClick={() => setShowHistory(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </Container>
    );
};

export default AdminPanel;